/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import type { MDSImageProps } from '../../../utils/mds-context'
import { useMDSImage } from '../../../utils/mds-context'
import { Badge } from '../../../components/badge'
import { Text } from '../../../components/text'
import { CardPrimitives } from '../primitives'
import type { CardPatternProps } from '../types'
import s from './style.module.css'

interface PartnerCardProps {
	heading: string
	description: string
	meta: string[]
	link: string
	productBadges?: CardPatternProps['productBadges']
	competencyBadges?: string[]
	logo: MDSImageProps
	className?: string
}

const PartnerCard = ({
	heading,
	description,
	meta,
	link,
	productBadges,
	competencyBadges,
	logo,
	className,
}: PartnerCardProps) => {
	const Image = useMDSImage()

	const hasProductBadges = productBadges && productBadges.length > 0
	const hasCompetencyBadges = competencyBadges && competencyBadges.length > 0

	return (
		<CardPrimitives.Root
			link={link}
			withArrow={false}
			isExternal={false}
			className={className}
			aria-label={heading}
		>
			<div className={s.logoContainer}>
				<Image {...logo} alt={logo.alt} className={s.logo} />
			</div>
			<CardPrimitives.Content>
				<CardPrimitives.Meta items={meta} />
				<Text.Body tag="p" size="200" className={s.description}>
					{description}
				</Text.Body>
				{hasProductBadges || hasCompetencyBadges ? (
					<CardPrimitives.ProductBadges badges={productBadges || []}>
						{hasCompetencyBadges &&
							competencyBadges.map((competencyBadge, stableIdx) => (
								<Badge key={stableIdx} text={competencyBadge} icon="dot" />
							))}
					</CardPrimitives.ProductBadges>
				) : null}
			</CardPrimitives.Content>
		</CardPrimitives.Root>
	)
}

PartnerCard.displayName = 'PartnerCard'

export type { PartnerCardProps }
export { PartnerCard }
